import { isSameUnitAsTarget, parseValueWithUnit } from '@/lib/units/unit-conversion'
import { MIN_GUTTER_POINTS, POINTS_PER_MM } from '../constants/guide-limits'
import type { GuideUnit } from '../types/guide-unit'
import { guideUnitToUnitOfMeasure } from './guide-unit'

export type ParsedGutterValue = {
  points: number | null
  decimalPlaces?: number
}

function parsePositiveNumber(raw: string): number | null {
  const trimmed = raw.trim()

  if (!/^\d+(?:\.\d+)?$/.test(trimmed)) return null
  const parsed = Number(trimmed)

  if (!Number.isFinite(parsed) || parsed <= 0) return null

  return parsed
}

export function parseGutterToPoints(raw: string, unit: GuideUnit): ParsedGutterValue {
  const trimmed = raw.trim()
  const targetUnit = guideUnitToUnitOfMeasure(unit)

  if (trimmed === '') {
    return { points: null }
  }

  if (unit === 'pica' && isInvalidPicaGutterInput(trimmed)) {
    return { points: null }
  }

  if (targetUnit) {
    const parsed = parseValueWithUnit(trimmed, targetUnit)

    if (parsed.error || !Number.isFinite(parsed.points) || parsed.points < MIN_GUTTER_POINTS) {
      return { points: null }
    }

    return {
      points: parsed.points,
      decimalPlaces:
        !parsed.hadUnit || isSameUnitAsTarget(parsed, targetUnit)
          ? parsed.decimalPlaces
          : undefined,
    }
  }

  const parsed = parsePositiveNumber(trimmed)

  if (parsed == null) return { points: null }

  const points = parsed * POINTS_PER_MM

  return {
    points: points >= MIN_GUTTER_POINTS ? points : null,
  }
}

function isInvalidPicaGutterInput(raw: string): boolean {
  const trimmed = raw.trim()

  if (trimmed === '') return false

  if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
    const parsed = Number(trimmed)

    return Number.isFinite(parsed) && parsed > 0 && parsed < 1
  }

  const picaCompositeMatch = trimmed.match(/^([\d.]+)\s*p\s*([\d.]*)\s*$/i)

  if (picaCompositeMatch) {
    const picaPart = Number(picaCompositeMatch[1])
    const pointPart = picaCompositeMatch[2]

    if (Number.isFinite(picaPart) && picaPart > 0 && picaPart < 1) {
      return true
    }

    if (
      picaPart === 0 &&
      pointPart !== '' &&
      (/^0(?:\.0+)?$/.test(pointPart) || /^0\.\d+$/.test(pointPart))
    ) {
      return true
    }
  }

  const picaSuffixMatch = trimmed.match(/^([\d.]+)\s*(picas|pica|p)\s*$/i)

  if (picaSuffixMatch) {
    const parsed = Number(picaSuffixMatch[1])

    return Number.isFinite(parsed) && parsed > 0 && parsed < 1
  }

  return false
}
